import * as React from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import {
    FormControl,
    Grid,
    InputLabel,
    ListItemButton,
    ListItemSecondaryAction,
    Typography,
    Stack,
    Tooltip,
    IconButton,
    Box,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
    AppFormikSelect,
    AppFormikTextField,
    AppRadioBox,
} from 'shared/components/AppComponents';
import InfoIcon from '@mui/icons-material/Info';

export default function FixList(props: ListProps): React.ReactElement {
    const {
        onViewDocument,
        filename,
        isWarned,
        defaultValue,
        index,
        name,
        categoryData,
        groupsData,
        canGroup,
        groupName,
        isCommon,
        groupsSelectionName,
        unexpectedFileType,
        showCategory,
        isSendToRework,
        onCheckedSendToRework,
    } = props;
    const theme = useTheme();
    const match = useMediaQuery(theme.breakpoints.up('md'));
    const handleRadioCheckbox = React.useCallback(() => {
        if (typeof onCheckedSendToRework === 'function') {
            onCheckedSendToRework(isSendToRework);
        }
    }, [isSendToRework, onCheckedSendToRework]);
    return (
        <ListItem
            className={
                isWarned || unexpectedFileType
                    ? 'categorise-selection'
                    : undefined
            }
        >
            <Grid container spacing={2} className="categorization-container">
                {(isWarned || unexpectedFileType) && (
                    <Grid item xs={12} className="report-categorise">
                        <Stack
                            direction="row"
                            spacing={2}
                            className="stack-report-notice"
                        >
                            <WarningAmberRoundedIcon />
                            <Typography>
                                {unexpectedFileType
                                    ? 'Unexpected file type is found in the packet'
                                    : `Can't categorise this document. Please select
                                the category manually`}
                            </Typography>
                        </Stack>
                    </Grid>
                )}
                <Grid
                    item
                    xs={12}
                    md={showCategory && canGroup === false ? 8 : 12}
                    lg={showCategory ? 8 : 12}
                    className="content-list"
                >
                    <Stack
                        direction={match ? 'row' : 'column'}
                        spacing={1}
                        display="flex"
                        flexGrow={1}
                    >
                        <Box className="categorization-checkbox">
                            <AppRadioBox
                                checked={isSendToRework}
                                disabled={isWarned || unexpectedFileType}
                                color="primary"
                                value="testt2"
                                onChange={handleRadioCheckbox}
                                label="Send to Rework"
                            />
                        </Box>
                        <Grid
                            container
                            direction="row"
                            className="content-container"
                        >
                            <Grid
                                item
                                xs={12}
                                md={8}
                                className="list-container"
                            >
                                <ListItemText
                                    primary={
                                        groupName
                                            ? `File Name (${groupName})`
                                            : 'File Name'
                                    }
                                    secondary={filename}
                                />
                            </Grid>
                            {unexpectedFileType === false ? (
                                <Grid
                                    item
                                    xs={12}
                                    md={4}
                                    className="button-container"
                                >
                                    <ListItemButton
                                        disabled={unexpectedFileType}
                                        onClick={() =>
                                            onViewDocument(index, filename)
                                        }
                                        aria-haspopup="dialog"
                                    >
                                        <ListItemText primary="View document" />
                                    </ListItemButton>
                                </Grid>
                            ) : (
                                <Grid
                                    item
                                    xs={12}
                                    md={4}
                                    className="button-container info-icon"
                                >
                                    <Tooltip
                                        placement="left-start"
                                        title="Expected File Types: PDF and Word document. Unexpected file types are not considered for Validation. Please click on save changes to Proceed. "
                                    >
                                        <IconButton>
                                            <InfoIcon fontSize="medium" />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            )}
                        </Grid>
                    </Stack>
                </Grid>
                {showCategory && (
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    <>
                        {canGroup === false ? (
                            <Grid
                                item
                                xs={12}
                                md={4}
                                className="categorise-selection"
                            >
                                <ListItemSecondaryAction>
                                    <FormControl fullWidth>
                                        <InputLabel id="fix-selection-label">
                                            Select a category
                                        </InputLabel>
                                        <AppFormikSelect
                                            testid="select-categorize"
                                            name={name}
                                            notched={false}
                                            disabled={unexpectedFileType}
                                            labelId="fix-selection-label"
                                            MenuProps={{
                                                className: 'menu-fix-selection',
                                            }}
                                            label="Category"
                                            defaultValue={defaultValue}
                                            items={categoryData}
                                        />
                                    </FormControl>
                                </ListItemSecondaryAction>
                            </Grid>
                        ) : (
                            <Grid
                                item
                                xs={12}
                                lg={4}
                                className="categorise-selection"
                            >
                                <Stack
                                    direction="row"
                                    className="categorise-action"
                                    spacing={2}
                                    alignItems="center"
                                >
                                    <FormControl fullWidth>
                                        <InputLabel id="fix-selection-label">
                                            {isCommon ? '' : 'Select group'}
                                        </InputLabel>
                                        <AppFormikSelect
                                            testid="select-groups"
                                            name={groupsSelectionName}
                                            multiple
                                            notched={false}
                                            disabled={
                                                unexpectedFileType ||
                                                groupName === 'Loan Document'
                                            }
                                            placeholder={
                                                isCommon
                                                    ? 'Group Common'
                                                    : undefined
                                            }
                                            labelId="fix-selection-label"
                                            MenuProps={{
                                                className: 'menu-fix-selection',
                                            }}
                                            items={groupsData}
                                        />
                                    </FormControl>
                                    <AppFormikTextField
                                        testid="select-categorize"
                                        name={name}
                                        disabled
                                        notched={false}
                                        labelId="fix-selection-label"
                                        MenuProps={{
                                            className: 'menu-fix-selection',
                                        }}
                                        defaultValue={defaultValue}
                                        items={categoryData}
                                    />
                                </Stack>
                            </Grid>
                        )}
                    </>
                )}
            </Grid>
        </ListItem>
    );
}
